import APIError from './errors/api_error';
export class BaseApi {
    constructor(url, tokenFetcher, openApiConfig) {
        this.url = url;
        this.tokenFetcher = tokenFetcher;
        this.openApiConfig = openApiConfig;
        /*
            This is an OpenAPI middleware to handle appending credentials to API calls from our CredentialManager.
        */
        this.preApiMiddleware = async (context) => {
            let authorizationValue;
            if (this.shareToken) {
                authorizationValue = `ShareToken ${this.shareToken}`;
            }
            else if (this.apiKey) {
                authorizationValue = `Basic ${btoa(`${this.apiKey.id}:${this.apiKey.secret}`)}`;
            }
            else if (this.adminBearerToken) {
                authorizationValue = `Bearer ${this.adminBearerToken}`;
            }
            else if (this.tokenFetcher?.isTokenAvailable()) {
                authorizationValue = `Bearer ${await this.tokenFetcher.fetchAccessToken()}`;
            }
            if (authorizationValue) {
                const headers = new Headers(context.init.headers);
                headers.set('Authorization', authorizationValue);
                context.init.headers = headers;
            }
            return context;
        };
        /*
            This is a OpenAPI middleware to handle retries if credentials need a refresh and raise API errors.
        */
        this.postApiMiddleware = async (context) => {
            if (context.response.status === 500) {
                throw new APIError('Internal Server Error', context.response);
            }
            // This is a hack to keep track of retries in the fetch RequestInit.
            const retries = context.init.retries || 0;
            // Skip retry logic for ShareToken and Basic auth.
            const authorizationHeader = this.getHeader(context.init, 'Authorization');
            const hasAuthHeaderWithBearerToken = authorizationHeader && authorizationHeader.startsWith('Bearer');
            // Refresh access token if unauthorized
            if (hasAuthHeaderWithBearerToken &&
                context.response.status === 401 &&
                retries <= 2 &&
                typeof window !== 'undefined') {
                await this.tokenFetcher?.refreshAccessToken();
                return context.fetch(context.url, this.buildRetryRequestParams(context, retries));
            }
            // Retry with backoff
            if (context.response.status === 429 && retries <= 4) {
                await this.delay(retries);
                return context.fetch(context.url, this.buildRetryRequestParams(context, retries));
            }
            if (context.response.status === 401 && retries > 2) {
                throw new APIError('Received 401 even after refreshing OAuth token.', context.response);
            }
            if (context.response.status === 429 && retries > 4) {
                throw new APIError('Received series of 429 even after backing off', context.response);
            }
            if (context.response.status < 200 || context.response.status >= 400) {
                const responseBody = await context.response.clone().text();
                throw new APIError(`Received error.status=${context.response.status} error.responseBody=<${responseBody}> error.url=<${context.response.url}>`, context.response);
            }
            return context.response;
        };
    }
    // Used by Streem's Support App
    setAdminBearerToken(token) {
        this.adminBearerToken = token;
    }
    setShareToken(token) {
        this.shareToken = token;
    }
    setApiKey(id, secret) {
        this.apiKey = {
            id,
            secret,
        };
    }
    get apiConfig() {
        if (!this.url) {
            throw new Error(`Attempted to use ${this.constructor.name} before calling ${this.constructor.name}.configure`);
        }
        return {
            ...(this.openApiConfig || {}),
            basePath: this.url,
            middleware: [
                {
                    pre: this.preApiMiddleware,
                    post: this.postApiMiddleware,
                },
            ],
        };
    }
    buildRetryRequestParams(context, retries) {
        return Object.assign(context.init, {
            retries: ++retries,
        });
    }
    /**
     * Sleeps program for up to 1 second max.
     */
    delay(retryCount) {
        const timeout = 10 ** retryCount;
        return new Promise(resolve => setTimeout(resolve, timeout > 1000 ? 1000 : timeout));
    }
    getHeader(init, name) {
        const headers = new Headers(init.headers);
        return headers.get(name);
    }
}
//# sourceMappingURL=base_api.js.map